import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';

import * as I from 'interfaces';

import {type CreateHarvest, type Harvest} from 'client';
import {gramsToKilos, localDateSender, tonsToKilos} from 'helpers/UnitsFormatHandler';
import {type AxiosError} from 'axios';
import Alert from 'helpers/Alert';

import {type TFunction} from 'i18next';
import {type Dispatch, type SetStateAction} from 'react';
import {actionTypeEnum, type IHarvest, type IHarvestAction} from '../../components/InputMenu/Harvest/reducer';
import type {SetRequired} from 'type-fest';

export const initialState = (formData?: I.IFormData): I.HarvestInitialState => {
  const biomass = formData?.cageId && formData?.biomass ? formData.biomass : 0;
  const count = formData?.cageId && formData?.count ? formData.count : 0;
  const fullHarvest = formData?.fullHarvest ?? Boolean(!formData?.id && formData?.siteId);
  const id = formData ? formData?.id : undefined;

  return {
    date: formData?.date ?? new Date(),
    originalDate: formData?.date ?? new Date(),
    biomass,
    allocatedBiomass: formData?.id ? biomass : 0,
    allocatedCount: formData?.id ? count : 0,
    count: formData?.cageId && formData?.count ? Math.floor(formData.count) : 0,
    weight: formData?.weight ?? 0,
    fullHarvest,
    isGraded: formData?.isGraded ?? false,
    cageId: formData?.cageId ?? '',
    siteId: formData?.siteId,
    isSequence: false,
    datesSequence: formData?.date ? [formData?.date] : [],
    id,
  };
};

export const handleCommunicationSuccess = ({
  setCreateUpdateCageHarvest,
  dispatch,
  cageId,
  date,
}: IHandleCommunicationSuccess) => {
  setCreateUpdateCageHarvest(false);
  const emptyHarvest = initialState(undefined);
  dispatch({type: actionTypeEnum.setProperties, ...emptyHarvest});
  void mutate([cageId, date?.getDate(), I.SWRKeyEnum.GET_CAGE_FORECAST]);
};

function validateState<T extends I.methodType>(
  method: T,
  state: IHarvest,
  t: TFunction,
): state is SetRequired<IHarvest, 'cageId'> & (T extends I.methodType.edit ? SetRequired<IHarvest, 'id'> : IHarvest) {
  const failConditions = [
    {condition: !state.cageId, message: t('noCageError')},
    {condition: !state.fullHarvest && state.biomass === 0, message: t('harvestCountError')},
    {condition: !state.date, message: t('noDate')},
    {condition: state.isSequence && state.datesSequence.length === 0, message: t('noDate')},
    {condition: method === I.methodType.edit && !state.id, message: t('noHarvestError')},
  ];

  for (const {condition, message} of failConditions) {
    if (condition) {
      Alert.warning(message);

      return false;
    }
  }

  return true;
}

function formatHarvest(state: SetRequired<IHarvest, 'cageId'>) {
  return {
    date: localDateSender(state.date),
    biomass:
      [undefined, 'biomass'].includes(state.typingField) && state.biomass ? tonsToKilos(state.biomass) : undefined,
    count: state.typingField === 'count' && state.count ? state.count : undefined,
    ...(state.isGraded && {weight: state.weight ? gramsToKilos(state.weight) : undefined}),
    cageId: state.cageId,
    fullHarvest: state.fullHarvest,
    isGraded: !state.fullHarvest ? state.isGraded : false,
    isEmergency: state.isEmergency,
    harvestPlantId: state.harvestPlantId ?? null,
  } as const satisfies CreateHarvest;
}

const backendOperator = async ({method, state, t}: {method: I.methodType; state: IHarvest; t: TFunction}) => {
  switch (method) {
    case I.methodType.add:
      if (!validateState(method, state, t)) {
        return null;
      }

      if (state.isSequence) {
        return await backendCommunicator.batchEditHarvests(state.cageId, {
          add: state.datesSequence.map((date) => formatHarvest({...state, date})),
        });
      }

      return await backendCommunicator.addHarvest(formatHarvest(state));
    case I.methodType.edit:
      if (!validateState(method, state, t)) {
        return null;
      }

      if (state.isSequence) {
        const [firstDate, ...restDates] = state.datesSequence;

        return await backendCommunicator.batchEditHarvests(state.cageId, {
          edit: [{...formatHarvest({...state, date: firstDate}), id: state.id}],
          add: restDates.map((date) => formatHarvest({...state, date})),
        });
      }

      return await backendCommunicator.updateHarvest(state.id, formatHarvest(state));
  }
};

const addEditHarvestBackendHandler = async ({
  setCreateUpdateCageHarvest,
  method,
  state,
  createUpdateCageHarvest,
  communicationSuccess,
  t,
}: IHandleCommunication) => {
  const backendOperation =
    createUpdateCageHarvest &&
    backendOperator({
      method,
      state,
      t,
    });
  const response =
    backendOperation &&
    backendOperation
      .then((response) => {
        if (response) {
          communicationSuccess(response.data);
        }
        return response;
      })
      .catch((error: AxiosError) => {
        Alert.error(error);
      })
      .finally(() => {
        setCreateUpdateCageHarvest(false);
      });
  return await response;
};

export default addEditHarvestBackendHandler;

interface IHandleCommunication {
  setCreateUpdateCageHarvest: Dispatch<SetStateAction<boolean>>;
  method: I.methodType;
  state: IHarvest;
  createUpdateCageHarvest: boolean;
  communicationSuccess: (harvest: Harvest | Harvest[]) => void;
  t: TFunction;
}

interface IHandleCommunicationSuccess {
  setCreateUpdateCageHarvest: Dispatch<SetStateAction<boolean>>;
  dispatch: Dispatch<IHarvestAction>;
  cageId: string | null;
  date: Date | null;
}
